<script setup lang="ts">
import { nextTick, onMounted, readonly, ref, useTemplateRef } from 'vue';
import { createCardPaymentSessionApi } from '@/api/cardPayments';
import { createHttpClient } from '@/api/http';
import { useExpiry } from '@/composables/useExpiry';
import { useHostEvents } from '@/composables/useHostEvents';
import { useMicroform } from '@/composables/useMicroform';
import { themeCssVars } from '@/theme';

const props = defineProps<{
  clientId: string;
}>();

const http = createHttpClient(props.clientId);
const { dispatch } = useHostEvents();
const expiry = useExpiry();
const microform = useMicroform({
  number: useTemplateRef<HTMLElement>('numberContainer'),
  securityCode: useTemplateRef<HTMLElement>('securityCodeContainer'),
});

const isReady = ref(false);
const errorMessage = ref('');
let pendingSubmit: Promise<string | null> | null = null;

function toErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function fail(message: string): void {
  errorMessage.value = message;
  dispatch('error', message);
}

async function runSubmit(): Promise<string | null> {
  errorMessage.value = '';

  const expiryDate = expiry.validate();
  if (!expiryDate) {
    fail('Enter a valid expiry date.');

    return null;
  }

  try {
    const transientToken = await microform.createToken(expiryDate);
    dispatch('success', transientToken);

    return transientToken;
  } catch (error) {
    fail(toErrorMessage(error, 'Unable to verify the card details. Please try again.'));

    return null;
  }
}

/**
 * Called by the merchant, e.g. `const token = await document.querySelector('ax-card-payment').submit()`.
 * Resolves the transient token for the merchant to capture server-side, or `null` on failure
 * (the reason is sent via the `error` event). Never rejects.
 * Calling again while a submit is in progress returns the same promise, so a double click yields one token.
 */
function submit(): Promise<string | null> {
  if (!isReady.value) {
    fail('Payment form is not ready yet.');

    return Promise.resolve(null);
  }

  pendingSubmit ??= runSubmit().finally(() => {
    pendingSubmit = null;
  });

  return pendingSubmit;
}

onMounted(async () => {
  try {
    const session = await createCardPaymentSessionApi(http);
    await microform.init(session);
  } catch (error) {
    fail(toErrorMessage(error, 'Unable to load the payment form.'));

    return;
  }

  if (!microform.isLoaded.value) {
    return;
  }

  // Vue attaches exposed members to the element right after mount; make sure that has
  // happened so `submit()` is callable from inside a `ready` listener.
  await nextTick();
  isReady.value = true;
  dispatch('ready');
});

defineExpose({
  submit,
  isReady: readonly(isReady),
});
</script>

<template>
  <div class="ax-card-payment" :style="themeCssVars">
    <label class="label">Card number</label>
    <div ref="numberContainer" class="field"></div>

    <div class="row">
      <div>
        <label class="label" for="exp">Expiry</label>
        <div
          class="field"
          :class="{
            'flex-microform-focused': expiry.isFocused.value,
            'flex-microform-invalid': expiry.isInvalid.value,
          }"
        >
          <input
            id="exp"
            :value="expiry.value.value"
            placeholder="MM / YY"
            inputmode="numeric"
            autocomplete="cc-exp"
            maxlength="7"
            @input="expiry.onInput"
            @focus="expiry.onFocus"
            @blur="expiry.onBlur"
          />
        </div>
      </div>
      <div>
        <label class="label">CVV</label>
        <div ref="securityCodeContainer" class="field"></div>
      </div>
    </div>

    <div class="errors" role="alert">{{ errorMessage }}</div>
  </div>
</template>

<style>
/*
 * Shadow DOM keeps our styles in, but inherited properties (font-size, letter-spacing, text-transform...)
 * still flow in from the merchant's page. Reset them at the host; merchants can still size/position
 * <ax-card-payment> from outside, since their rules on the element win over :host.
 * Not scoped: Vue would turn this into `:host[data-v-...]`, which never matches.
 */
:host {
  all: initial;
  display: block;
}
</style>

<style scoped>
.ax-card-payment {
  font-family: var(--ax-font);
  color: var(--ax-text);
}

.label {
  display: block;
  font-size: 14px;
  margin: 12px 0 6px;
}

.row {
  display: flex;
  gap: 12px;
}

.row > div {
  flex: 1;
}

/* One wrapper style for all three fields */
.field {
  box-sizing: border-box;
  height: 44px;
  padding: 0 12px;
  border: 1px solid var(--ax-border);
  border-radius: 6px;
  background: #fff;
  display: flex;
  align-items: center;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}

/* Iframes are injected by Microform, so they don't get the scoped attribute */
.field :deep(iframe) {
  width: 100%;
}

/* Microform adds these to its containers; we add them to expiry ourselves */
.field.flex-microform-focused {
  border-color: var(--ax-focus);
  box-shadow: 0 0 0 3px rgb(0 112 210 / 0.15);
}

.field.flex-microform-invalid {
  border-color: var(--ax-error);
}

/* Expiry input mimics the iframe contents: no chrome, same type */
.field input {
  all: unset;
  width: 100%;
  font-family: var(--ax-font);
  font-size: var(--ax-font-size);
  line-height: var(--ax-line-height);
  color: var(--ax-text);
}

.field input::placeholder {
  color: var(--ax-placeholder);
}

.errors {
  color: var(--ax-error);
  font-size: 14px;
  min-height: 20px;
  margin-top: 12px;
}
</style>
